import { isDefined } from '@poseidon-city/utils';
import axios from 'axios';

import { TApiSDKOptions } from './main';

type TAPIClient = {
  setAuthToken(accessToken: string): void;
  post<TPBody, TPResult>(path: string, body?: TPBody): Promise<TPResult | undefined>;
  put<TPBody, TPResult>(path: string, body?: TPBody): Promise<TPResult | undefined>;
  get<TPResult>(path: string): Promise<TPResult | undefined>;
  delete(path: string): Promise<void>;
};

type TRequestIdService = {
  /**
   * Generates a unique ID for each request
   * @return {string} Unique ID (length: 20 characters)
   */
  getUniqueId(): string;
};

type TLanguageService = {
  /**
   * Returns the currently selected language (e.g., 'en', 'cs')
   */
  getSelectedLanguage(): string;
};

function APIClient(
  options: TApiSDKOptions,
  requestIdService: TRequestIdService,
  languageService: TLanguageService
): TAPIClient {
  let baseURL = `${options.host}${options.pathPrefix}`;

  if (baseURL.endsWith('/')) {
    baseURL = baseURL.slice(0, -1);
  }

  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  let _accessToken: string | undefined;

  client.interceptors.request.use((config) => {
    if (isDefined(_accessToken)) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }

    config.headers['X-Request-Id'] = requestIdService.getUniqueId();
    config.headers['Accept-Language'] = languageService.getSelectedLanguage();

    return config;
  });

  const request = async (method: string, url: string, data?: any): Promise<any> => {
    const response = await client.request({
      method,
      url,
      data,
    });

    if (response.status >= 400) {
      console.error(`APIClient.${method} '${url}' failed`, response);
      return;
    }

    return response.data;
  };

  return {
    setAuthToken(accessToken: string): void {
      _accessToken = accessToken;
    },

    async post<TPBody, TPResult>(path: string, body: TPBody): Promise<TPResult | undefined> {
      return request('post', path, body) as TPResult | undefined;
    },

    async put<TPBody, TPResult>(path: string, body: TPBody): Promise<TPResult | undefined> {
      return request('put', path, body) as TPResult | undefined;
    },

    async get<TPResult>(path: string): Promise<TPResult | undefined> {
      return request('get', path) as TPResult | undefined;
    },

    async delete(path: string): Promise<void> {
      return request('delete', path);
    },
  };
}

export { APIClient };
export type { TAPIClient, TRequestIdService, TLanguageService };
